import { supabase } from "@/integrations/supabase/client";

interface Course {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: string;
  price: number;
  instructor_name: string;
  rating: number;
  students_count: number;
  thumbnail_url: string;
  is_coming_soon?: boolean;
  estimated_launch_date?: string;
  early_bird_discount?: number;
  discount_percentage?: number;
  test_mode?: boolean;
}

interface CachedData<T> {
  data: T;
  timestamp: number;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, CachedData<any>>();

function getCached<T>(key: string): T | null {
  const cached = cache.get(key);
  if (!cached) return null;
  
  if (Date.now() - cached.timestamp > CACHE_DURATION) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
}

function setCache<T>(key: string, data: T): void {
  cache.set(key, { data, timestamp: Date.now() });
}

export async function fetchAllCourses(): Promise<Course[]> {
  const cacheKey = "all_courses";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;
  
  const { data, error } = await supabase
    .from("courses")
    .select("*")
    .eq("is_published", true);
  
  if (error) throw error;
  
  const courses = data || [];
  setCache(cacheKey, courses);
  return courses;
}

export async function fetchPopularCourses(): Promise<Course[]> {
  const cacheKey = "popular_courses";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;
  
  // Simplified query - just get top courses by students_count
  const { data, error } = await supabase
    .from("courses")
    .select("*")
    .eq("is_published", true)
    .order("students_count", { ascending: false })
    .limit(6);
  
  if (error) throw error;
  
  const courses = data || [];
  setCache(cacheKey, courses);
  return courses;
}

export async function fetchFeaturedCourses(): Promise<Course[]> {
  const cacheKey = "featured_courses";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;
  
  const { data, error } = await supabase
    .from("courses")
    .select("*")
    .eq("is_published", true)
    .order("rating", { ascending: false })
    .limit(3);
  
  if (error) throw error;
  
  const courses = data || [];
  setCache(cacheKey, courses);
  return courses;
}

export async function fetchCategories(): Promise<string[]> {
  const cacheKey = "categories";
  const cached = getCached<string[]>(cacheKey);
  
  if (cached) return cached;
  
  const { data, error } = await supabase
    .from("course_categories")
    .select("name")
    .eq("is_active", true)
    .order("display_order", { ascending: true });
  
  if (error) throw error;
  
  const categories = (data || []).map(cat => cat.name);
  setCache(cacheKey, categories);
  return categories;
}

export async function fetchAllCoursesWithPromotions(): Promise<Course[]> {
  const cacheKey = "courses_with_promotions";
  const cached = getCached<Course[]>(cacheKey);
  
  if (cached) return cached;

  const now = new Date().toISOString();
  
  // Fetch all courses with their promotions in a single query
  const { data: coursesData, error: coursesError } = await supabase
    .from("courses")
    .select(`
      *,
      promotion_courses!left(
        promotion:promotions!inner(
          id,
          name,
          discount_percentage,
          start_date,
          end_date,
          is_active
        )
      )
    `)
    .or("is_published.eq.true,is_coming_soon.eq.true,syllabus->>status.eq.coming_soon")
    .order("created_at", { ascending: false });

  if (coursesError) throw coursesError;

  // Fetch site-wide promotion separately
  const { data: siteWidePromo, error: promoError } = await supabase
    .from("promotions")
    .select(`
      id,
      name,
      discount_percentage,
      start_date,
      end_date,
      is_active,
      promotion_courses(course_id)
    `)
    .eq("is_active", true)
    .lte("start_date", now)
    .gte("end_date", now)
    .limit(1)
    .maybeSingle();

  if (promoError && promoError.code !== "PGRST116") throw promoError;

  // Check if site-wide promo has no specific courses
  const isSiteWide = siteWidePromo && (!siteWidePromo.promotion_courses || siteWidePromo.promotion_courses.length === 0);

  // Process courses with promotion data
  const courses = (coursesData || []).map((course: any) => {
    const coursePromos = course.promotion_courses || [];
    let discountPercentage = 0;

    // Check course-specific promotions first
    for (const pc of coursePromos) {
      if (pc.promotion && pc.promotion.is_active) {
        const startDate = new Date(pc.promotion.start_date);
        const endDate = new Date(pc.promotion.end_date);
        const currentDate = new Date();
        
        if (currentDate >= startDate && currentDate <= endDate) {
          discountPercentage = Math.max(discountPercentage, pc.promotion.discount_percentage);
        }
      }
    }

    // If no course-specific promo, apply site-wide
    if (discountPercentage === 0 && isSiteWide) {
      discountPercentage = siteWidePromo.discount_percentage;
    }

    // Remove the promotion_courses field from the final object
    const { promotion_courses, ...courseData } = course;

    return {
      ...courseData,
      discount_percentage: discountPercentage,
    };
  });

  setCache(cacheKey, courses);
  return courses;
}

export function clearCourseCache(): void {
  cache.clear();
}

export interface MyEnrollment {
  id: string;
  enrolled_at: string;
  completed_at: string | null;
  progress: number;
  /**
   * ISO timestamp when time-limited access ends. `null` = lifetime access.
   * Set by payment webhooks based on the course's `access_type` /
   * `access_duration_days` policy.
   */
  access_expires_at: string | null;
  course: Course & { is_published?: boolean };
}

/**
 * Fetch all enrollments for a logged-in user, joined with their course rows.
 * Used by the in-shell `/my-courses` page.
 */
export async function fetchMyEnrollments(userId: string): Promise<MyEnrollment[]> {
  const { data, error } = await supabase
    .from("enrollments")
    .select(`
      id,
      enrolled_at,
      completed_at,
      progress,
      access_expires_at,
      course:courses (
        id,
        slug,
        title,
        description,
        category,
        level,
        duration,
        price,
        instructor_name,
        rating,
        students_count,
        thumbnail_url,
        is_published,
        is_coming_soon
      )
    `)
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("enrolled_at", { ascending: false });

  if (error) throw error;

  return (data || [])
    .filter((row: any) => row.course) // course may be null if soft-deleted
    .map((row: any) => ({
      id: row.id,
      enrolled_at: row.enrolled_at,
      completed_at: row.completed_at,
      progress: typeof row.progress === "number" ? row.progress : 0,
      access_expires_at: row.access_expires_at ?? null,
      course: row.course,
    }));
}

/* ───────────── Course details (panel) ───────────── */

export interface CourseDetails {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string;
  level: string;
  duration: string;
  price: number;
  instructor_name: string;
  rating: number;
  students_count: number;
  thumbnail_url: string;
  is_published?: boolean;
  is_coming_soon?: boolean;
  is_free?: boolean;
  pricing_type?: string | null;
  accepts_stripe?: boolean;
  accepts_pix?: boolean;
  test_mode?: boolean;
  discount_percentage?: number;
  lessons_count: number;
}

export async function fetchCourseDetails(courseId: string): Promise<CourseDetails | null> {
  const { data: course, error } = await supabase
    .from("courses")
    .select(
      "id, slug, title, description, category, level, duration, price, instructor_name, rating, students_count, thumbnail_url, is_published, is_coming_soon, is_free, pricing_type, accepts_stripe, accepts_pix, test_mode"
    )
    .eq("id", courseId)
    .maybeSingle();

  if (error) throw error;
  if (!course) return null;

  const { count } = await supabase
    .from("lessons")
    .select("id", { count: "exact", head: true })
    .eq("course_id", courseId)
    .eq("is_published", true);

  return { ...(course as any), lessons_count: count ?? 0 };
}

/* ───────────── Enrollment progress mutations ───────────── */

export async function updateEnrollmentProgress(
  enrollmentId: string,
  progress: number
): Promise<void> {
  const clamped = Math.min(100, Math.max(0, Math.round(progress)));
  const patch: Record<string, unknown> = { progress: clamped };
  if (clamped >= 100) patch.completed_at = new Date().toISOString();
  else patch.completed_at = null;

  const { error } = await supabase
    .from("enrollments")
    .update(patch)
    .eq("id", enrollmentId);
  if (error) throw error;
}

export async function markEnrollmentCompleted(enrollmentId: string): Promise<void> {
  const { error } = await supabase
    .from("enrollments")
    .update({ progress: 100, completed_at: new Date().toISOString() })
    .eq("id", enrollmentId);
  if (error) throw error;
}

export async function reopenEnrollment(enrollmentId: string): Promise<void> {
  const { error } = await supabase
    .from("enrollments")
    .update({ completed_at: null, progress: 0 })
    .eq("id", enrollmentId);
  if (error) throw error;
}
